(() => {
  const WIDGET_SELECTOR = '[data-custom-skio-widget]';

  const normalizeString = (value = '') => value.toLowerCase().replace(/\s+/g, ' ').trim();
  const extractNumericTokens = (value = '') => value.match(/\d+/g) || [];
  const CADENCE_TO_DAYS = {
    day: 1,
    days: 1,
    week: 7,
    weeks: 7,
    month: 30,
    months: 30,
  };
  const toInteger = (value, fallback = 0) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const parseCents = (value) => {
    if (value === null || value === undefined || value === '') return null;

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const extractQuantity = (value = '') => {
    const normalizedValue = String(value || '').trim();
    const quantityMatch = normalizedValue.match(/(\d+)\s*(bag|bags|box|boxes)\b/i);
    if (quantityMatch) return toInteger(quantityMatch[1], null);

    const numericOnlyMatch = normalizedValue.match(/^(\d+)$/);
    return numericOnlyMatch ? toInteger(numericOnlyMatch[1], null) : null;
  };
  const isQuantityLabel = (value = '') => extractQuantity(value) !== null;
  const splitLines = (value = '') =>
    value
      .split('||')
      .map((line) => line.trim())
      .filter(Boolean);
  const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, (character) => HTML_ESCAPES[character]);

  const formatMoney = (cents) => {
    if (!Number.isFinite(cents)) return '';

    try {
      return new Intl.NumberFormat(navigator.language || 'en-US', {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };
  const namesMatch = (requestedName = '', candidateName = '') => {
    const normalizedRequested = normalizeString(requestedName);
    const normalizedCandidate = normalizeString(candidateName);

    if (!normalizedRequested || !normalizedCandidate) return false;
    if (normalizedRequested === normalizedCandidate) return true;

    if (normalizedCandidate.includes(normalizedRequested) || normalizedRequested.includes(normalizedCandidate)) {
      return true;
    }

    const requestedNumericTokens = extractNumericTokens(normalizedRequested);
    return requestedNumericTokens.length > 0 && requestedNumericTokens.every((token) => normalizedCandidate.includes(token));
  };
  const getCadenceDays = (value = '') => {
    const normalizedValue = normalizeString(value);
    const cadenceMatch = normalizedValue.match(/(\d+)\s*(day|days|week|weeks|month|months)/);
    if (!cadenceMatch) return null;

    const cadenceCount = toInteger(cadenceMatch[1], 0);
    const cadenceUnit = CADENCE_TO_DAYS[cadenceMatch[2]];
    if (!cadenceCount || !cadenceUnit) return null;

    return cadenceCount * cadenceUnit;
  };
  const findCadenceMatch = (requestedName = '', candidates = []) => {
    const requestedDays = getCadenceDays(requestedName);
    if (!requestedDays) return null;

    const nearestMatch = candidates
      .map((candidate) => ({
        ...candidate,
        cadenceDays: getCadenceDays(candidate.name),
      }))
      .filter((candidate) => candidate.cadenceDays !== null)
      .sort((left, right) => Math.abs(left.cadenceDays - requestedDays) - Math.abs(right.cadenceDays - requestedDays))[0];

    if (!nearestMatch) return null;
    if (Math.abs(nearestMatch.cadenceDays - requestedDays) > 7) return null;

    return nearestMatch;
  };

  class CustomSkioWidget {
    constructor(root) {
      this.root = root;
      this.root.dataset.customSkioWidgetInitialized = 'true';

      this.formId = this.root.dataset.formId;
      this.hasSellingPlans = this.root.dataset.hasSellingPlans === 'true';
      this.defaultOptionKey = this.root.dataset.defaultOption;
      this.oneTimeOptionKey = this.root.dataset.oneTimeOption || this.defaultOptionKey;
      this.form = document.getElementById(this.formId);
      this.section = this.root.closest('product-info') || this.root.closest('.product__info-container') || document;
      this.optionButtons = [...this.root.querySelectorAll('[data-custom-skio-option]')];
      this.modeButtons = [...this.root.querySelectorAll('[data-custom-skio-mode]')];
      this.mixFlavorsLink = this.root.querySelector('[data-custom-skio-mix-flavors]');
      this.quantityInput =
        this.root.querySelector('[data-custom-skio-quantity-input]') || this.form?.querySelector('input[name="quantity"]');
      this.nativeFieldset = null;
      this.nativeBlock = null;
      this.nativeSubscriptionRadio = null;
      this.nativeOneTimeRadio = null;
      this.nativeSelect = null;
      this.sellingPlanInput = null;
      this.productJsonNode = null;
      this.productData = null;
      this.currentOption = null;
      this.currentMode = this.hasSellingPlans ? 'subscription' : 'one-time';
      this.lastSubscriptionOption = null;
      this.hasModeInteraction = false;
      this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      this.freeGiftsByOption = this.parseFreeGiftsJson();

      this.handleOptionClick = this.handleOptionClick.bind(this);
      this.handleModeClick = this.handleModeClick.bind(this);
      this.handleSectionChange = this.handleSectionChange.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleCartUpdate = this.handleCartUpdate.bind(this);

      this.optionButtons.forEach((button) => button.addEventListener('click', this.handleOptionClick));
      this.modeButtons.forEach((button) => button.addEventListener('click', this.handleModeClick));
      this.section.addEventListener('change', this.handleSectionChange);
      this.form?.addEventListener('submit', this.handleFormSubmit, true);
      subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);

      this.refresh({ syncModeFromNative: true });
      this.hideNativeWidgetIfNeeded();
    }

    parseProductData() {
      if (!this.productJsonNode) return null;

      try {
        return JSON.parse(this.productJsonNode.textContent);
      } catch (error) {
        return null;
      }
    }

    parseFreeGiftsJson() {
      const node = this.root.querySelector('script[data-custom-skio-free-gifts-json]');
      if (!node) return {};

      try {
        return JSON.parse(node.textContent) || {};
      } catch (error) {
        return {};
      }
    }

    getFreeGifts(optionButton) {
      const key = optionButton?.dataset.optionKey;
      return (key && this.freeGiftsByOption[key]) || [];
    }

    ensureSellingPlanInput() {
      if (!this.form) return null;

      let input = this.form.querySelector('input[name="selling_plan"]');
      if (input) return input;

      // Keep a form-owned selling_plan input so add-to-cart does not depend on Skio's async markup.
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      input.dataset.customSkioManaged = 'true';
      this.form.appendChild(input);
      return input;
    }

    refreshNativeReferences() {
      const managedSellingPlanInput = this.form?.querySelector('input[name="selling_plan"][data-custom-skio-managed]');

      this.nativeFieldset = this.section.querySelector('fieldset[skio-plan-picker]');
      this.nativeBlock = this.nativeFieldset ? this.nativeFieldset.closest('.shopify-app-block') : null;
      this.nativeSubscriptionRadio = this.nativeFieldset
        ? this.nativeFieldset.querySelector('input[skio-selling-plan-group]')
        : null;
      this.nativeOneTimeRadio = this.nativeFieldset ? this.nativeFieldset.querySelector('input[skio-one-time]') : null;
      this.nativeSelect = this.nativeFieldset ? this.nativeFieldset.querySelector('select[skio-selling-plans]') : null;
      const nativeSellingPlanInput =
        (this.nativeFieldset &&
          this.nativeFieldset.querySelector(`input[name="selling_plan"][form="${this.formId}"]`)) ||
        document.querySelector(`input[name="selling_plan"][form="${this.formId}"]`);

      if (nativeSellingPlanInput && managedSellingPlanInput && nativeSellingPlanInput !== managedSellingPlanInput) {
        managedSellingPlanInput.remove();
      }

      this.sellingPlanInput =
        this.form?.querySelector('input[name="selling_plan"]') ||
        nativeSellingPlanInput ||
        (this.hasSellingPlans ? this.ensureSellingPlanInput() : null);
      // Prefer the theme-rendered product JSON so pricing data stays stable when Skio mutates its own widget markup.
      this.productJsonNode =
        this.root.querySelector('script[data-custom-skio-product-json]') ||
        (this.nativeFieldset && this.nativeFieldset.querySelector('script[skio-product-json]'));
      this.productData = this.parseProductData();
    }

    resolveInitialMode() {
      if (!this.hasSellingPlans) return 'one-time';
      if (this.nativeOneTimeRadio?.checked) return 'one-time';
      if (this.sellingPlanInput?.value || this.nativeSubscriptionRadio?.checked) return 'subscription';
      return this.modeButtons.find((button) => button.dataset.customSkioMode === 'subscription') ? 'subscription' : 'one-time';
    }

    getCurrentVariant() {
      if (!this.productData || !this.productData.variants || !this.productData.variants.length) return null;
      const variantId = this.form ? this.form.querySelector('[name="id"]')?.value : null;
      return (
        this.productData.variants.find((variant) => String(variant.id) === String(variantId)) ||
        this.productData.variants[0]
      );
    }

    getVariantQuantity(variant) {
      if (!variant) return null;

      return (
        extractQuantity(variant.option2) ||
        extractQuantity(variant.option1) ||
        extractQuantity(variant.option3) ||
        extractQuantity(variant.title)
      );
    }

    getVariantFlavorKey(variant) {
      if (!variant) return null;

      const candidates = [variant.option1, variant.option2, variant.option3];
      return candidates.find((value) => value && !isQuantityLabel(value)) || null;
    }

    getPricingVariant(optionButton) {
      if (!this.productData?.variants?.length) return null;

      const requestedQuantity = Math.max(toInteger(optionButton.dataset.quantity, 1), 1);
      const currentVariant = this.getCurrentVariant();
      const currentFlavor = this.getVariantFlavorKey(currentVariant);
      const variants = this.productData.variants;

      const flavorMatches = currentFlavor
        ? variants.filter((variant) => this.getVariantFlavorKey(variant) === currentFlavor)
        : variants;
      const candidates = flavorMatches.length ? flavorMatches : variants;

      return (
        candidates.find((variant) => this.getVariantQuantity(variant) === requestedQuantity) ||
        variants.find((variant) => this.getVariantQuantity(variant) === requestedQuantity) ||
        currentVariant ||
        variants[0]
      );
    }

    getSellingPlans() {
      if (!this.productData || !this.productData.selling_plan_groups) return [];

      return this.productData.selling_plan_groups.flatMap((group) =>
        (group.selling_plans || []).map((plan) => ({
          id: String(plan.id),
          name: plan.name,
        }))
      );
    }

    getSelectOptions() {
      if (!this.nativeSelect) return [];

      return [...this.nativeSelect.options]
        .filter((option) => option.value)
        .map((option) => ({
          id: String(option.value),
          name: option.textContent.trim(),
        }));
    }

    getPlanMatch(optionButton) {
      if (!this.hasSellingPlans) return null;

      // Match Variety tiers against market-local prices, not the USD display settings.
      // Do not fall back to a different cadence or discount for this product.
      if (this.root.dataset.varietyPack === 'true') {
        const variant = this.getPricingVariant(optionButton);
        const quantity = Math.max(toInteger(optionButton.dataset.quantity, 1), 1);
        const discountPercent = { 1: 15, 2: 20, 3: 25 }[quantity];
        const basePrice = parseCents(variant?.price);
        if (!discountPercent || !basePrice) return null;
        const expectedUnitCents = basePrice * (1 - discountPercent / 100);
        const plan = this.getSellingPlans().find((candidate) => {
          const allocation = this.getPlanAllocation(variant, candidate.id);
          const price = parseCents(allocation?.price);
          return getCadenceDays(candidate.name) === 28 && price !== null &&
            Math.abs(price - expectedUnitCents) <= 1;
        });
        return plan ? { ...plan, matchType: 'name' } : null;
      }

      const requestedName = normalizeString(optionButton.dataset.planName);
      const requestedPosition = toInteger(optionButton.dataset.planPosition, 1) - 1;
      const sellingPlans = this.getSellingPlans();
      const selectOptions = this.getSelectOptions();

      if (requestedName) {
        const planMatch = sellingPlans.find((plan) => namesMatch(requestedName, plan.name));
        if (planMatch) return { ...planMatch, matchType: 'name' };

        const cadencePlanMatch = findCadenceMatch(requestedName, sellingPlans);
        if (cadencePlanMatch) return { ...cadencePlanMatch, matchType: 'cadence' };

        const selectMatch = selectOptions.find((option) => namesMatch(requestedName, option.name));
        if (selectMatch) return { ...selectMatch, matchType: 'name' };

        const cadenceSelectMatch = findCadenceMatch(requestedName, selectOptions);
        if (cadenceSelectMatch) return { ...cadenceSelectMatch, matchType: 'cadence' };
      }

      if (!requestedName && sellingPlans[requestedPosition]) {
        return { ...sellingPlans[requestedPosition], matchType: 'position' };
      }
      if (!requestedName && selectOptions[requestedPosition]) {
        return { ...selectOptions[requestedPosition], matchType: 'position' };
      }

      if (sellingPlans[0]) return { ...sellingPlans[0], matchType: 'default' };
      if (selectOptions[0]) return { ...selectOptions[0], matchType: 'default' };

      return null;
    }

    getPlanAllocation(variant, planId) {
      if (!variant || !variant.selling_plan_allocations || !variant.selling_plan_allocations.length) return null;

      return variant.selling_plan_allocations.find(
        (allocation) => String(allocation.selling_plan_id) === String(planId)
      ) || null;
    }

    getOptionState(optionButton) {
      const variant = this.getPricingVariant(optionButton);
      const planMatch = this.getPlanMatch(optionButton);
      const quantity = Math.max(toInteger(optionButton.dataset.quantity, 1), 1);
      const canUseLivePlanPricing = planMatch && (planMatch.matchType === 'name' || planMatch.matchType === 'position');
      const allocation = canUseLivePlanPricing ? this.getPlanAllocation(variant, planMatch && planMatch.id) : null;
      const basePriceCents = parseCents(variant?.price) ?? parseCents(this.productData?.price);
      const allocationPriceCents = parseCents(allocation?.price);
      const subscriptionCents = allocationPriceCents !== null ? allocationPriceCents * quantity : null;
      const comparePriceCents = basePriceCents !== null ? basePriceCents * quantity : null;
      const hasPlanPricing = this.hasSellingPlans && Boolean(planMatch || this.getSellingPlans().length);
      const summaryOverride = optionButton.dataset.summary?.trim();
      const subscriptionPriceOverride = optionButton.dataset.subscriptionPriceOverride?.trim();
      const subscriptionCompareOverride = optionButton.dataset.subscriptionComparePriceOverride?.trim();
      const oneTimePriceOverride = optionButton.dataset.oneTimePriceOverride?.trim();
      const captionOverride = optionButton.dataset.cardCaption?.trim();
      const detailLines = hasPlanPricing ? splitLines(optionButton.dataset.detailLines || '') : [];
      const title =
        optionButton.querySelector('.custom-skio-widget__option-title')?.textContent.trim() ||
        optionButton.dataset.optionKey;
      const hasLiveComparePrice = comparePriceCents !== null;
      const hasLiveSubscriptionPrice = canUseLivePlanPricing && subscriptionCents !== null;
      const liveSummaryText =
        hasLiveComparePrice && hasLiveSubscriptionPrice && comparePriceCents > subscriptionCents
          ? `Save ${formatMoney(comparePriceCents - subscriptionCents)}`
          : '';

      const isVarietyPack = this.root.dataset.varietyPack === 'true';
      const subscriptionPriceText = hasPlanPricing
        ? hasLiveSubscriptionPrice
          ? formatMoney(subscriptionCents)
          : subscriptionPriceOverride || (hasLiveComparePrice ? formatMoney(comparePriceCents) : '')
        : hasLiveComparePrice
          ? formatMoney(comparePriceCents)
          : oneTimePriceOverride || '';
      const subscriptionCompareText = hasPlanPricing
        ? hasLiveComparePrice
          ? formatMoney(comparePriceCents)
          : subscriptionCompareOverride || ''
        : '';
      const oneTimePriceText = hasLiveComparePrice ? formatMoney(comparePriceCents) : oneTimePriceOverride || '';
      const summaryText =
        hasPlanPricing
          ? (isVarietyPack ? summaryOverride || liveSummaryText : liveSummaryText || summaryOverride) || ''
          : '';
      const captionText = isVarietyPack && hasLiveSubscriptionPrice
        ? `${formatMoney(subscriptionCents / (quantity * 10))}/serving`
        : captionOverride || (canUseLivePlanPricing && planMatch ? planMatch.name : '');
      const freeGifts = this.getFreeGifts(optionButton);

      return {
        quantity,
        planId: planMatch ? String(planMatch.id) : '',
        planName: planMatch ? planMatch.name : '',
        title,
        captionText,
        summaryText,
        subscriptionPriceText,
        subscriptionCompareText,
        oneTimePriceText,
        detailLines,
        freeGifts,
      };
    }

    refreshOptionCards() {
      this.optionButtons.forEach((button) => {
        const state = this.getOptionState(button);
        const priceNode = button.querySelector('[data-custom-skio-render="card-price"]');
        const compareNode = button.querySelector('[data-custom-skio-render="card-compare-price"]');
        const captionNode = button.querySelector('[data-custom-skio-render="card-caption"]');
        const overridePrice = button.dataset.subscriptionPriceOverride?.trim();
        const overrideCompare = button.dataset.subscriptionComparePriceOverride?.trim();
        // Prefer live product/selling-plan pricing so admin price changes do not
        // require matching manual edits in the theme block settings.
        const cardPriceText = state.subscriptionPriceText || overridePrice;
        const cardCompareText = state.subscriptionCompareText || overrideCompare;

        if (priceNode) priceNode.textContent = cardPriceText;

        if (compareNode) {
          const compareVisible =
            cardCompareText && cardCompareText !== cardPriceText;
          compareNode.textContent = compareVisible ? cardCompareText : '';
          compareNode.classList.toggle('is-hidden', !compareVisible);
        }

        if (captionNode) {
          captionNode.textContent = state.captionText;
          captionNode.classList.toggle('is-hidden', !state.captionText);
        }
      });
    }

    getInitialOption() {
      return this.getOptionByKey(this.defaultOptionKey) || this.optionButtons[0] || null;
    }

    getOptionByKey(key) {
      return this.optionButtons.find((button) => button.dataset.optionKey === key) || null;
    }

    getOneTimeOption() {
      const configuredOption = this.getOptionByKey(this.oneTimeOptionKey);

      if (configuredOption && toInteger(configuredOption.dataset.quantity, 0) === 1) {
        return configuredOption;
      }

      return (
        this.optionButtons.find((button) => toInteger(button.dataset.quantity, 0) === 1) ||
        configuredOption ||
        this.optionButtons[this.optionButtons.length - 1] ||
        null
      );
    }

    getOneTimeModeButton() {
      return this.modeButtons.find((button) => button.dataset.customSkioMode === 'one-time') || null;
    }

    isOneTimeAvailable(optionButton = this.currentOption) {
      if (!this.hasSellingPlans) return true;

      const oneTimeOption = this.getOneTimeOption();

      return Boolean(
        optionButton &&
        oneTimeOption &&
        optionButton.dataset.optionKey === oneTimeOption.dataset.optionKey &&
        toInteger(optionButton.dataset.quantity, 0) === 1
      );
    }

    renderOneTimeAvailability(optionButton = this.currentOption) {
      const oneTimeButton = this.getOneTimeModeButton();
      if (!oneTimeButton) return;

      const isAvailable = this.isOneTimeAvailable(optionButton);

      oneTimeButton.hidden = !isAvailable;
      oneTimeButton.classList.toggle('is-hidden', !isAvailable);
    }

    refresh({ syncModeFromNative = false } = {}) {
      this.refreshNativeReferences();
      if (!this.optionButtons.length) return;

      if (syncModeFromNative) this.currentMode = this.resolveInitialMode();
      if (!this.hasSellingPlans) this.currentMode = 'one-time';

      this.refreshOptionCards();
      this.currentOption = this.currentOption || this.getInitialOption();
      if (!this.currentOption) return;

      if (this.currentMode === 'one-time' && !this.isOneTimeAvailable(this.currentOption)) {
        this.currentMode = 'subscription';
      }

      this.setSelectedOption(this.currentOption, { syncNative: false });

      if (!this.modeButtons.find((button) => button.dataset.customSkioMode === this.currentMode)) {
        this.currentMode = 'subscription';
      }

      this.setMode(this.currentMode, { syncNative: false });
      this.syncNativeState();
    }

    setSelectedOption(optionButton, { syncNative = true, rememberSubscriptionSelection = this.currentMode === 'subscription' } = {}) {
      this.currentOption = optionButton;
      const optionState = this.getOptionState(optionButton);

      if (rememberSubscriptionSelection) {
        this.lastSubscriptionOption = optionButton;
      }

      this.optionButtons.forEach((button) => {
        const isSelected = button === optionButton;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-checked', String(isSelected));
      });

      if (this.quantityInput) this.quantityInput.value = String(optionState.quantity);

      this.renderDeliveryState(optionState);
      this.renderMixFlavorsState(optionState);
      this.renderOneTimeAvailability(optionButton);

      if (syncNative) this.syncNativeState();
    }

    setMode(mode, { syncNative = true } = {}) {
      if (!this.hasSellingPlans) mode = 'one-time';

      if (mode === 'one-time') {
        if (this.currentMode !== 'one-time' && this.currentOption) {
          this.lastSubscriptionOption = this.currentOption;
        }

        if (this.hasSellingPlans) {
          const oneTimeOption = this.getOneTimeOption();
          if (oneTimeOption && this.currentOption !== oneTimeOption) {
            this.setSelectedOption(oneTimeOption, {
              syncNative: false,
              rememberSubscriptionSelection: false,
            });
          }
        }
      } else {
        const subscriptionOption = this.lastSubscriptionOption || this.getInitialOption();
        if (subscriptionOption && this.currentOption !== subscriptionOption) {
          this.setSelectedOption(subscriptionOption, {
            syncNative: false,
            rememberSubscriptionSelection: true,
          });
        }
      }

      this.currentMode = mode;

      this.modeButtons.forEach((button) => {
        const isSelected = button.dataset.customSkioMode === mode;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-checked', String(isSelected));
      });

      if (this.currentOption) {
        this.renderMixFlavorsState(this.getOptionState(this.currentOption));
        this.renderOneTimeAvailability(this.currentOption);
      }

      if (syncNative) this.syncNativeState();
    }

    renderDeliveryState(optionState) {
      const summaryNode = this.root.querySelector('[data-custom-skio-render="summary"]');
      const subscriptionPriceNode = this.root.querySelector('[data-custom-skio-render="subscription-price"]');
      const subscriptionCompareNode = this.root.querySelector(
        '[data-custom-skio-render="subscription-compare-price"]'
      );
      const detailsNode = this.root.querySelector('[data-custom-skio-render="details"]');
      const oneTimePriceNode = this.root.querySelector('[data-custom-skio-render="one-time-price"]');
      const oneTimeMetaNode = this.root.querySelector('[data-custom-skio-render="one-time-meta"]');

      if (summaryNode) {
        summaryNode.textContent = optionState.summaryText;
        summaryNode.classList.toggle('is-hidden', !optionState.summaryText);
      }

      if (subscriptionPriceNode) subscriptionPriceNode.textContent = optionState.subscriptionPriceText;

      if (subscriptionCompareNode) {
        const compareVisible =
          optionState.subscriptionCompareText && optionState.subscriptionCompareText !== optionState.subscriptionPriceText;
        subscriptionCompareNode.textContent = compareVisible ? optionState.subscriptionCompareText : '';
        subscriptionCompareNode.classList.toggle('is-hidden', !compareVisible);
      }

      if (detailsNode) {
        detailsNode.innerHTML = optionState.detailLines.map((line) => `<li>${line}</li>`).join('');
        detailsNode.classList.toggle('is-hidden', optionState.detailLines.length === 0);
      }

      this.renderFreeGifts(optionState);

      if (oneTimePriceNode) oneTimePriceNode.textContent = optionState.oneTimePriceText;

      if (oneTimeMetaNode) {
        oneTimeMetaNode.textContent = optionState.title;
        oneTimeMetaNode.classList.toggle('is-hidden', !optionState.title);
      }
    }

    renderMixFlavorsState(optionState) {
      if (!this.mixFlavorsLink) return;

      const shouldShow = this.currentMode === 'subscription' && optionState.quantity > 1;
      this.mixFlavorsLink.classList.toggle('is-hidden', !shouldShow);
    }

    renderFreeGifts(optionState) {
      const container = this.root.querySelector('[data-custom-skio-render="free-gifts"]');
      const listNode = this.root.querySelector('[data-custom-skio-render="free-gifts-list"]');
      if (!container || !listNode) return;

      const gifts = optionState.freeGifts || [];
      listNode.innerHTML = gifts
        .map((gift) => {
          const thumb = gift.image
            ? `<img class="custom-skio-widget__free-gifts-thumb" src="${escapeHtml(gift.image)}" width="40" height="40" loading="lazy" alt="">`
            : '';
          return `<li>${thumb}<span>${escapeHtml(gift.title)}</span></li>`;
        })
        .join('');
      container.classList.toggle('is-hidden', gifts.length === 0);
    }

    syncNativeState() {
      this.refreshNativeReferences();
      if (!this.currentOption) return;

      const optionState = this.getOptionState(this.currentOption);

      if (this.currentMode === 'one-time') {
        if (this.nativeOneTimeRadio) {
          this.nativeOneTimeRadio.checked = true;
          this.dispatchValueEvents(this.nativeOneTimeRadio);
        }

        if (this.sellingPlanInput) {
          this.sellingPlanInput.value = '';
          this.dispatchValueEvents(this.sellingPlanInput);
        }
      } else {
        if (this.nativeSubscriptionRadio) {
          this.nativeSubscriptionRadio.checked = true;
          this.dispatchValueEvents(this.nativeSubscriptionRadio);
        }

        if (this.nativeSelect && optionState.planId) {
          this.nativeSelect.value = optionState.planId;
          this.dispatchValueEvents(this.nativeSelect);
        }

        if (this.sellingPlanInput) {
          this.sellingPlanInput.value = optionState.planId || (this.nativeSelect ? this.nativeSelect.value : '');
          this.dispatchValueEvents(this.sellingPlanInput);
        }
      }

      this.updateNativeClasses();
    }

    updateNativeClasses() {
      if (!this.nativeFieldset) return;

      const subscriptionContainer = this.nativeSubscriptionRadio
        ? this.nativeSubscriptionRadio.closest('[skio-group-container]')
        : null;
      const oneTimeContainer = this.nativeOneTimeRadio ? this.nativeOneTimeRadio.closest('[skio-group-container]') : null;
      const subscriptionSelected = this.currentMode === 'subscription';

      subscriptionContainer?.classList.toggle('skio-group-container--selected', subscriptionSelected);
      oneTimeContainer?.classList.toggle('skio-group-container--selected', !subscriptionSelected);
    }

    hideNativeWidgetIfNeeded() {
      if (this.root.dataset.hideNative !== 'true' || !this.nativeBlock) return;
      this.nativeBlock.classList.add('custom-skio-widget__native-hidden');
    }

    dispatchValueEvents(element) {
      if (!element) return;
      element.dispatchEvent(new Event('input', { bubbles: true }));
      element.dispatchEvent(new Event('change', { bubbles: true }));
    }

    handleOptionClick(event) {
      event.preventDefault();
      const optionButton = event.currentTarget;

      if (
        this.hasSellingPlans &&
        this.currentMode === 'one-time' &&
        optionButton.dataset.optionKey !== this.oneTimeOptionKey
      ) {
        this.setSelectedOption(optionButton, {
          syncNative: false,
          rememberSubscriptionSelection: true,
        });
        this.setMode('subscription');
        return;
      }

      this.setSelectedOption(optionButton);
    }

    handleModeClick(event) {
      event.preventDefault();
      this.hasModeInteraction = true;
      this.setMode(event.currentTarget.dataset.customSkioMode);
    }

    handleSectionChange(event) {
      const target = event.target;
      if (!target) return;

      if (
        target.name === 'id' ||
        target.matches('variant-selects select, variant-radios input') ||
        target.closest('variant-selects, variant-radios')
      ) {
        window.requestAnimationFrame(() => {
          this.refreshNativeReferences();
          this.refreshOptionCards();
          if (this.currentOption) {
            const optionState = this.getOptionState(this.currentOption);
            this.renderDeliveryState(optionState);
            this.renderMixFlavorsState(optionState);
          }
        });
      }
    }

    handleFormSubmit(event) {
      if (this.root.dataset.varietyPack === 'true' && this.currentMode === 'subscription' &&
          !this.getOptionState(this.currentOption).planId) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.form?.closest('product-form')?.handleErrorMessage(
          'This subscription option is temporarily unavailable. Please try another option.'
        );
        return;
      }
      this.syncNativeState();
      this.pendingGiftSubmission = {
        variantId: this.form?.querySelector('[name="id"]')?.value,
        planId: this.sellingPlanInput?.value,
        giftVariantIds: this.currentMode === 'subscription' ? this.getFreeGiftVariantIds(this.currentOption) : [],
        quantity: this.getOptionState(this.currentOption).quantity,
      };
    }

    // Free gifts ship once with the initial subscription order only: they are plain
    // cart line items (no selling_plan), so Skio never carries them into recurring orders.
    getFreeGiftVariantIds(optionButton) {
      return this.getFreeGifts(optionButton)
        .map((gift) => gift.variantId)
        .filter(Boolean);
    }

    handleCartUpdate(payload) {
      if (!payload || payload.source !== 'product-form' || payload.formId !== this.formId) return;
      const submission = this.pendingGiftSubmission;
      this.pendingGiftSubmission = null;
      if (!submission?.planId || !submission.giftVariantIds.length) return;

      const addedItems = payload.cartData?.items || [payload.cartData];
      const subscriptionAdded = addedItems.some((item) =>
        String(item?.variant_id || item?.id) === String(submission.variantId) &&
        String(item?.selling_plan_allocation?.selling_plan?.id) === String(submission.planId)
      );
      if (!subscriptionAdded) return;
      return this.addFreeGifts(submission.giftVariantIds, submission.variantId, submission.quantity)
        .catch((error) => console.error('[custom-skio-widget] Failed to verify free gifts', error));
    }

    // _subscription_gift_min_qty lets subscription-gifts.js drop gifts if the subscription is reduced in the cart.
    async addFreeGifts(giftVariantIds, parentVariantId, minQuantity = 1) {
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      if (!cartResponse.ok) throw new Error('Unable to verify gift eligibility');
      const cart = await cartResponse.json();
      if (!cart.items.some((item) => String(item.variant_id) === String(parentVariantId) && item.selling_plan_allocation)) return;
      // Only existing gift lines count, so a paid copy of the same product doesn't block the gift.
      giftVariantIds = [...new Set(giftVariantIds)].filter((id) =>
        !cart.items.some((item) => String(item.variant_id) === String(id) && item.properties?._subscription_gift_parent)
      );
      if (!giftVariantIds.length) return;
      const properties = { _subscription_gift_parent: String(parentVariantId), _subscription_gift_min_qty: String(minQuantity) };
      const config = fetchConfig('json');
      config.body = JSON.stringify({
        items: giftVariantIds.map((variantId) => ({ id: Number(variantId), quantity: 1, properties })),
        sections: this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [],
        sections_url: window.location.pathname,
      });

      return fetch(routes.cart_add_url, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            console.error('[custom-skio-widget] Failed to add free gifts', response.errors || response.description);
            return;
          }

          if (this.cart) this.cart.renderContents(response);
        })
        .catch((error) => console.error('[custom-skio-widget] Failed to add free gifts', error));
    }
  }

  const initWidgets = (scope = document) => {
    const widgets = scope.matches?.(WIDGET_SELECTOR)
      ? [scope]
      : [...scope.querySelectorAll?.(WIDGET_SELECTOR) || []];

    widgets.forEach((widget) => {
      if (widget.dataset.customSkioWidgetInitialized === 'true') return;
      widget.__customSkioWidget = new CustomSkioWidget(widget);
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    initWidgets();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          initWidgets(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  });

  document.addEventListener('shopify:section:load', (event) => initWidgets(event.target));
  document.addEventListener('skio:widget-loaded', () => {
    initWidgets();
    document.querySelectorAll(WIDGET_SELECTOR).forEach((widget) => {
      const customSkioWidget = widget.__customSkioWidget;
      if (!customSkioWidget) return;

      customSkioWidget.refresh({ syncModeFromNative: !customSkioWidget.hasModeInteraction });
      customSkioWidget.hideNativeWidgetIfNeeded();
    });
  });
})();
